import React, { useState, useEffect, useMemo } from 'react';
import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';

// Import form create ticket
import EscalateTicket from '@/component/ticketList/EscalateTicket';
import QaTicket from '@/component/ticketList/QaTicket';
import HelpdeskCreateTicket from '@/component/ticketList/HelpdeskCreateTicket';
import LoadingPanel from '@/component/common/LoadingPanel';

// Recoil
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { userAtom } from '@/state/atom/userAtom';
import { projectInuseDetailAtom } from '@/state/atom/projectAtom';
import { multiPanelAtom } from '@/state/atom/panelAtom';
import {
    closePanelTabSelector
} from '@/state/selector/panelSelector';
import {
    triggerReLoadKnowledgeTicketDetailAtom
} from '@/state/atom/ticketKnowledgeAtom';
import { triggerReLoadHelpdeskTicketDetailAtom } from '@/state/atom/ticketAtom';

import { getTicketDetailAPI } from '@/service/ticketService';
import KnowledgeTicket from './KnowledgeTicket';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import { handleConfirmAccessDenied } from '../ticketKnowledge/handler/DetailTicketHandler';
import PropTypes from 'prop-types';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';

export default function DetailTicketDraft(props) {
    // Re-render when the translation dictionary loads/updates
    useTranslationSubscription();

    // common userDetail state
    const userDetail = useRecoilValue(userAtom);
    // common projectInuse state
    const projectInuseDetail = useRecoilValue(projectInuseDetailAtom);
    const panelList = useRecoilValue(multiPanelAtom);

    const closePanelTab = useSetRecoilState(closePanelTabSelector);

    const [triggerReLoadKnowledgeTicketDetail, setTriggerReLoadKnowledgeTicketDetail] = useRecoilState(
        triggerReLoadKnowledgeTicketDetailAtom
    );

    const [
        triggerReLoadHelpdeskTicketDetail,
        setTriggerReLoadHelpdeskTicketDetail
    ] = useRecoilState(triggerReLoadHelpdeskTicketDetailAtom);

    const [isLoading, setIsLoading] = useState(false);
    const [isModalUploadFileError, setIsModalUploadFileError] = useState(false);
    const [draftData, setDraftData] = useState({});
    const [isOpenAccessDeniedPopup, setIsOpenAccessDeniedPopup] = useState(false);

    const argsAll = {
        ...props,
        isModalUploadFileError,
        setIsModalUploadFileError,
        draftData,
        detailTicket: draftData.detailTicket,
        tabId: props.isDuplicate ? props.tabId : draftData?.detailTicket?.id,
        isSoftDeleted: !Utils.isEmptyString(draftData?.detailTicket?.deletedAt),
        setTriggerReLoadHelpdeskTicketDetail: setTriggerReLoadHelpdeskTicketDetail
    }

    const handleGetTicketDraftDetail = async (isMounted) => {
        if (!isMounted) return;
        if (!props.ticketId) return;
        setIsLoading(true);

        getTicketDetailAPI(
            props?.API,
            props.ticketId,
            projectInuseDetail.id,
            userDetail.id,
            false
        ).then(data => {
            if (!isMounted) return;
            if (!data?.detailTicket) {
                setIsOpenAccessDeniedPopup(true);
                return;
            }
            if (data.detailTicket?.public !== Constant.TICKET_PUBLIC.DRAFT && !props.isDuplicate) {
                closePanelTab({
                    event: null,
                    panelType: Utils.getPanelData(props.panelId, panelList).type,
                    panelId: props.panelId,
                    tabId: props.ticketId
                });
                return;
            }

            if (props.isDuplicate && (!data?.detailTicket || data?.detailTicket.deletedAt)) {
                setIsOpenAccessDeniedPopup(true);
                return;
            }

            let ticketDetail = data.detailTicket;
            let bookmarkData = JSON.parse(ticketDetail?.bookmarkInfo);

            ticketDetail.privateBookmarkData = bookmarkData?.privateBookmarkData;
            ticketDetail.shareBookmarkData = bookmarkData?.shareBookmarkData;
            ticketDetail.totalPrivateBookmark = bookmarkData?.totalPrivateBookmark;
            ticketDetail.isBookmarkedDefaultBookmark = bookmarkData?.isBookmarkedDefaultBookmark;
            ticketDetail.bookmarkListText = bookmarkData?.bookmarkListText;
            ticketDetail.attachFiles = Utils.convertJSONDataToObject(data?.attachFiles);

            delete ticketDetail.bookmarkInfo;

            data = {
                ...data,
                detailTicket: ticketDetail
            };

            setDraftData(data);
        }).catch(error => {
            console.log('DetailTicketDraft getTicketDetailAPI error :>>', error)
        }).finally(() => {
            setIsLoading(false);
        });
    }
    
    // handle get init ticketDetail
    useEffect(() => {
        let isMounted = true;
        handleGetTicketDraftDetail(isMounted);

        return () => {
            isMounted = false;
        };
    }, []);

    const bookmarkTargetType = useMemo(() => {
        return Utils.getBookmarkTargetTypeByTicketType(draftData?.detailTicket?.ticketType);
    }, [draftData?.detailTicket?.ticketType]);

    // handle get init ticketDetail when have trigger reload
    useEffect(() => {
        if (
            props.triggerReload ||
            triggerReLoadKnowledgeTicketDetail === props.ticketId ||
            triggerReLoadHelpdeskTicketDetail === props.ticketId
        ) {
            handleGetTicketDraftDetail(true) 
            if (bookmarkTargetType === Constant.BOOKMARK_TARGET_TYPE.KNOWLEDGE) {
                setTriggerReLoadKnowledgeTicketDetail('');
            } else {
                setTriggerReLoadHelpdeskTicketDetail('');
            }
        }
    }, [
        props.triggerReload,
        triggerReLoadKnowledgeTicketDetail,
        triggerReLoadHelpdeskTicketDetail
    ]);

    const renderDetailTicket = () => {
        switch (draftData?.detailTicket?.ticketType) {
            case Constant.TICKET_TYPE.QA: 
            case Constant.TICKET_TYPE.INTERNAL_QA: 
            case Constant.TICKET_TYPE.EXTERNAL_QA: 
            case Constant.TICKET_TYPE.INVITATION_GUEST_DESK_PROJECT: 
            case Constant.TICKET_TYPE.REQUEST_EXTERNAL_DESK: 
                return <QaTicket {...argsAll} />
            case Constant.TICKET_TYPE.DELEGATE: 
            case Constant.TICKET_TYPE.NOTICE: 
            case Constant.TICKET_TYPE.INVITATION_GUEST_PROJECT: 
            case Constant.TICKET_TYPE.BROADCAST_PARENT: 
            case Constant.TICKET_TYPE.BROADCAST_CHILD: 
            case Constant.TICKET_TYPE.REQUEST_EXTERNAL_CUSTOMER: 
            case Constant.TICKET_TYPE.TASK:
                return <HelpdeskCreateTicket {...{...argsAll, selectedTicketType: draftData?.detailTicket?.ticketType}} />
            case Constant.TICKET_TYPE.ESCALATION:
                return <EscalateTicket {...argsAll} />
            case Constant.TICKET_TYPE.KNOWLEDGE:
            case Constant.TICKET_TYPE.MASTER_KNOWLEDGE:
                return <KnowledgeTicket {...argsAll} />
            case Constant.TICKET_TYPE.EMAIL:
                return <HelpdeskCreateTicket {...{...argsAll, selectedTicketType: draftData?.detailTicket?.ticketType}} />
            default: {
                return <></>;
            }
        }
    };

    return (
        <>
            {isLoading ? (
                <LoadingPanel isLoading={isLoading} />
            ) : (
                renderDetailTicket()
            )}
            <ConfirmDialog
                isOpen={isOpenAccessDeniedPopup}
                msgConfirm={trI18n('KNOWDEMSG005')}
                okCallBack={e => handleConfirmAccessDenied(setIsOpenAccessDeniedPopup)}
                textSubmit={trI18n('CM-BUTTON-OK')}
            />
        </>
    );
}

DetailTicketDraft.propTypes = {
    triggerReload: PropTypes.any,
    ticketId: PropTypes.string,
}